import React, { useMemo, useRef, useState } from "react";
import { classNames, doNothing } from "@greeter/util";

import {
  RejectSwiper,
  RejectStage,
} from "@greeter-venue/components/RejectSwiper/RejectSwiper";
import { IonIcon, IonModal } from "@ionic/react";

import { closeOutline } from "ionicons/icons";
import { MessageButtonList } from "./MessageButtonList";
import { MessageButton } from "./MessageButton";

import { MessageBox } from "./MessageBox";
import { useTimeoutManager } from "@greeter-venue/utility/Hooks";

import css from "./RejectModal.module.scss";

export type RejectModalProps = Omit<
  React.HTMLAttributes<HTMLIonModalElement>,
  "children"
> & {
  className?: string;
  onClose?: () => void;
  onReject?: (message: string) => void | Promise<void>;
  isOpen?: boolean;
  timeoutBeforeClose?: number;
  template?: (msg: string) => Promise<string>;
};

export const RejectModal: React.FC<RejectModalProps> = ({
  className,
  timeoutBeforeClose = 1000,
  onClose = doNothing,
  onReject = doNothing,
  isOpen = false,
  template = async (msg: string) => msg,
  ...props
}) => {
  const [rejectStage, setRejectStage] = useState<RejectStage>("unrejected");
  const [message, setMessage] = useState("");
  const [addTimeout] = useTimeoutManager();
  const contentWrapperRef = useRef<HTMLDivElement>(null);

  const isMessageApproved = useMemo(
    () => !!message && message.split(" ").length > 5 && message.length <= 255,
    [message]
  );

  return (
    <IonModal
      isOpen={isOpen}
      {...classNames(
        css.RejectModal,
        Array.isArray(className)
          ? classNames(...className).className
          : className
      )}
    >
      <div
        ref={contentWrapperRef}
        {...classNames("inner-content", css.ContentWrapper)}
      >
        <div className={css.TopBar}>
          <button onClick={onClose} className={css.CloseButton}>
            <IonIcon icon={closeOutline} />
          </button>
        </div>
        <h1 className={css.Title}>Afvis ordre?</h1>
        <h4 className={css.MessageButtonListTitle}>
          Vælg et hurtig-svar til kunden
        </h4>
        <MessageButtonList>
          <MessageButton
            title="Alt er booket"
            message={`Hej {{customer}}.\n\nAlt er desværre booket og vi bliver nød til at afvise dig. Kom igen en anden gang.\n\nMvh, {{venueAdmin}}\n{{venue}}`}
            onClick={(m) => template(m).then(setMessage)}
          />
          <MessageButton
            title="Bordet er reserveret"
            message={`Hej {{customer}}.\n\nBordet er desværre allerede reserveret. Det er en fejl på vores side og det beklager vi meget.\n\nMvh, {{venueAdmin}}\n{{venue}}`}
            onClick={(m) => template(m).then(setMessage)}
          />
          <MessageButton
            title="Vi holder lukket"
            message={`Hej {{customer}}.\n\nVi holder lukket på det tidspunkt du har anmodet om og vi bliver derfor nød til at afvise din ordre.\n\nMvh, {{venueAdmin}}\n{{venue}}`}
            onClick={(m) => template(m).then(setMessage)}
          />
          <MessageButton
            title="Tilføj gæstens navn"
            message={`{{customer}}`}
            onClick={(m) => template(m).then((m) => setMessage(message + m))}
          />
          <MessageButton
            title="Betaling gik ikke igennem"
            message={`Hej {{customer}}.\n\nVi afviser ordren da betalingen ikke er gået igennem.\n\nMvh, {{venueAdmin}}\n{{venue}}`}
            onClick={(m) => template(m).then((m) => setMessage(message + m))}
          />
        </MessageButtonList>
        <MessageBox
          placeholder={`Beskriv hvorfor ordren afvises... \n(Tryk evt. på et hurtig-svar)`}
          value={message}
          onChange={(e: any) => {
            if (message.length < 255 || e.target.value.length < 255)
              setMessage(e.target.value);
          }}
        />
        <RejectSwiper
          disabled={!isMessageApproved}
          stage={rejectStage}
          onReject={async () => {
            setRejectStage("loading");
            try {
              await onReject(message);
              setRejectStage("done");
              addTimeout(() => {
                onClose();
                setRejectStage("unrejected");
              }, timeoutBeforeClose);
            } catch {
              setRejectStage("unrejected");
            }
          }}
        />
      </div>

      {/* </IonContent> */}
    </IonModal>
  );
};
